import { makeAutoObservable, observable, runInAction } from "mobx";
import { type AnalyticsSource, track } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import { IssueActivityStore } from "./issue-activity.store";
import { IssueAttachmentsStore } from "./issue-attachments.store";
import { IssueCommentsStore } from "./issue-comments.store";
import { IssueRelationsStore } from "./issue-relations.store";
import { IssueSubIssuesStore } from "./issue-sub-issues.store";
import type { TIssue } from "@/types/issue";

export class IssueDetailStore {
  currentIssueId: string | null = null;
  currentProjectId: string | null = null;
  isLoading = false;
  error: string | null = null;
  loadFailed = false;
  subscriptionByIssueId = observable.map<string, boolean>();

  comments: IssueCommentsStore;
  activity: IssueActivityStore;
  attachments: IssueAttachmentsStore;
  relations: IssueRelationsStore;
  subIssues: IssueSubIssuesStore;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.comments = new IssueCommentsStore(rootStore);
    this.activity = new IssueActivityStore(rootStore);
    this.attachments = new IssueAttachmentsStore(rootStore);
    this.relations = new IssueRelationsStore(rootStore);
    this.subIssues = new IssueSubIssuesStore(rootStore);
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get currentIssue(): TIssue | undefined {
    return this.currentIssueId ? this.rootStore.issue.getIssueById(this.currentIssueId) : undefined;
  }

  /** Always reads the full record — see `IssueStore.fetchIssueByIdentifier`. */
  async loadByIdentifier(workspaceSlug: string, workItem: string): Promise<TIssue> {
    this.isLoading = true;
    this.error = null;
    this.loadFailed = false;
    try {
      const issue = await this.rootStore.issue.fetchIssueByIdentifier(workspaceSlug, workItem);
      runInAction(() => {
        this.currentIssueId = issue.id;
        this.currentProjectId = issue.project_id ?? null;
        this.subscriptionByIssueId.set(issue.id, Boolean(issue.is_subscribed));
        this.isLoading = false;
      });

      return issue;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const isNotFound = status === 404;
      if (!isNotFound) {
        console.error("Failed to load work item", { workspaceSlug, workItem, error });
      }
      runInAction(() => {
        this.error = "Failed to load issue";
        this.loadFailed = !isNotFound;
        this.isLoading = false;
      });
      throw new Error("Failed to load issue");
    }
  }

  /**
   * Record that a user opened one work item (NG-2056): its page, the quick
   * view or a side panel. Hosts call this once the record has resolved, so a
   * failed or not-found load never counts as a view. `source` names the
   * surface it was opened from.
   */
  trackViewed(issue: TIssue, source: AnalyticsSource): void {
    track("task_viewed", {
      task_id: issue.id,
      project_id: issue.project_id ?? null,
      category: issue.category ?? null,
      source,
    });
  }

  setCurrentIssue(issueId: string | null, projectId: string | null = null): void {
    this.currentIssueId = issueId;
    this.currentProjectId = projectId;
  }

  resetCurrentIssue(): void {
    this.currentIssueId = null;
    this.currentProjectId = null;
    this.error = null;
    this.loadFailed = false;
  }

  async refreshCurrentIssue(workspaceSlug: string): Promise<void> {
    if (!this.currentIssueId || !this.currentProjectId) return;
    const issue = await this.rootStore.workItemService.getWorkItem(
      workspaceSlug,
      this.currentProjectId,
      this.currentIssueId,
    );
    runInAction(() => {
      this.rootStore.issue.upsertIssue(issue);
      this.subscriptionByIssueId.set(issue.id, Boolean(issue.is_subscribed));
    });
  }

  async toggleSubscription(workspaceSlug: string): Promise<void> {
    if (!this.currentIssueId || !this.currentProjectId) return;
    await this.toggleSubscriptionForIssue(
      workspaceSlug,
      this.currentProjectId,
      this.currentIssueId,
      "page",
    );
  }

  async toggleSubscriptionForIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const previous = this.subscriptionByIssueId.get(issueId) ?? false;

    this.subscriptionByIssueId.set(issueId, !previous);

    try {
      if (previous) {
        await this.rootStore.workItemService.unsubscribe(workspaceSlug, projectId, issueId);
      } else {
        await this.rootStore.workItemService.subscribe(workspaceSlug, projectId, issueId);
      }
      track(previous ? "task_unsubscribed" : "task_subscribed", {
        task_id: issueId,
        project_id: projectId,
        source,
      });
    } catch (error) {
      runInAction(() => {
        this.subscriptionByIssueId.set(issueId, previous);
      });
      throw error;
    }
  }
}
